import type React from 'react';

import { Chip, ChipGroup } from '@change/ds-core-ui/Chip';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';

import { useI18n } from '@change-corgi/core-react/i18n';

type Category = {
	name: string;
	slug: string;
};

type Props = {
	categories: ReadonlyArray<{ name: string | null; slug: string }>;
	// omitted on pages with no notion of a "current" category (e.g. the landing page), where
	// the All chip always shows and no category is excluded from the list
	currentSlug?: string;
	onCategoryClick: (categorySlug: string) => void;
};

export const CategoryChips: React.FC<Props> = ({ categories, currentSlug, onCategoryClick }) => {
	const { translate } = useI18n();

	const showAllChip = currentSlug !== 'all';
	const visibleCategories = categories.filter(
		// categories without a name would render their raw slug as user-visible copy; the
		// current category is dropped too, so its own chip never appears alongside itself
		(category): category is Category => !!category.name && category.slug !== currentSlug,
	);

	if (visibleCategories.length === 0 && !showAllChip) {
		return null;
	}

	return (
		<nav aria-label={translate('corgi.petition_guides_shared.category_chips.group_label')}>
			<ChipGroup size="small">
				{showAllChip && (
					<Chip
						as="a"
						href="/petition-guides/category/all"
						onClick={() => onCategoryClick('all')}
						trailingIcon={<ArrowRightIcon size="small" />}
					>
						{translate('corgi.petition_guides_shared.category_chips.all_label')}
					</Chip>
				)}
				{visibleCategories.map(({ name, slug }) => (
					<Chip
						key={slug}
						as="a"
						href={`/petition-guides/category/${slug}`}
						onClick={() => onCategoryClick(slug)}
						trailingIcon={<ArrowRightIcon size="small" />}
					>
						{name}
					</Chip>
				))}
			</ChipGroup>
		</nav>
	);
};
